import * as React from "react";

import Image from "next/image";

import { Building2 } from "lucide-react";

import { cn } from "@/shared/lib/utils";

type EntityAvatarEntity = {
  name?: string | null;
  logo_sm?: string | null;
  logo_big?: string | null;
};

type EntityAvatarProps = {
  entity: EntityAvatarEntity | null;
  size?: "sm" | "lg";
  className?: string;
  /** Prefer `logo_big` over `logo_sm`. Defaults to true on the large size and false on the small size. */
  preferLarge?: boolean;
  fallbackIcon?: React.ComponentType<{ className?: string }>;
};

export function EntityAvatar({
  entity,
  size = "lg",
  className,
  preferLarge,
  fallbackIcon: FallbackIcon = Building2,
}: EntityAvatarProps) {
  const usePreferLarge = preferLarge ?? size === "lg";
  const primary = usePreferLarge ? entity?.logo_big : entity?.logo_sm;
  const secondary = usePreferLarge ? entity?.logo_sm : entity?.logo_big;
  const initialSrc = primary ?? secondary ?? null;
  const imageSize = size === "sm" ? 36 : 64;

  const [src, setSrc] = React.useState<string | null>(initialSrc);

  React.useEffect(() => {
    setSrc(initialSrc);
  }, [initialSrc]);

  const sizeClasses = size === "sm" ? "size-9 rounded-md text-[11px]" : "size-16 rounded-lg text-sm";
  const iconClass = size === "sm" ? "size-4" : "size-6";
  const padding = size === "sm" ? "p-0.5" : "p-1";

  if (src) {
    return (
      <Image
        src={src}
        alt={entity?.name ?? ""}
        width={imageSize}
        height={imageSize}
        className={cn("bg-card ring-foreground/10 shrink-0 object-contain ring-1", sizeClasses, padding, className)}
        onError={() => {
          if (src !== secondary && secondary) {
            setSrc(secondary);
          } else {
            setSrc(null);
          }
        }}
      />
    );
  }

  const initials = getEntityInitials(entity?.name);

  return (
    <div
      className={cn(
        "bg-card text-muted-foreground ring-foreground/10 flex shrink-0 items-center justify-center font-semibold tracking-wide ring-1",
        sizeClasses,
        className,
      )}
      aria-hidden={!initials}
    >
      {initials ? initials : <FallbackIcon className={iconClass} />}
    </div>
  );
}

export function getEntityInitials(name?: string | null) {
  if (!name) {
    return "";
  }

  const parts = name.trim().split(/\s+/).filter(Boolean).slice(0, 2);

  if (!parts.length) {
    return "";
  }

  return parts.map((part) => part[0]?.toUpperCase() ?? "").join("");
}
